/**
 * Hava durumu — Open-Meteo (ücretsiz, API key yok, CORS açık).
 *
 * Şehir → enlem/boylam (bundled TR il listesi) → Open-Meteo current weather.
 * Cache: bellek + localStorage (offline'da son değer). TTL 20dk (7/24 ekran için hafif).
 * Hata/çevrimdışı → null (gösterilmez, ekran kırılmaz).
 */

export interface WeatherData {
    temp: number;
    code: number;
}

/** TR il merkezleri (lat, lon). Editör bu listeyi dropdown'da gösterir. */
export const TR_CITIES: Record<string, { lat: number; lon: number }> = {
    'İstanbul': { lat: 41.01, lon: 28.98 },
    'Ankara': { lat: 39.93, lon: 32.85 },
    'İzmir': { lat: 38.42, lon: 27.14 },
    'Bursa': { lat: 40.19, lon: 29.06 },
    'Antalya': { lat: 36.9, lon: 30.7 },
    'Adana': { lat: 37.0, lon: 35.32 },
    'Konya': { lat: 37.87, lon: 32.48 },
    'Gaziantep': { lat: 37.07, lon: 37.38 },
    'Şanlıurfa': { lat: 37.17, lon: 38.79 },
    'Kocaeli': { lat: 40.77, lon: 29.92 },
    'Mersin': { lat: 36.81, lon: 34.64 },
    'Diyarbakır': { lat: 37.91, lon: 40.24 },
    'Kayseri': { lat: 38.73, lon: 35.49 },
    'Eskişehir': { lat: 39.78, lon: 30.52 },
    'Samsun': { lat: 41.29, lon: 36.33 },
    'Denizli': { lat: 37.77, lon: 29.09 },
    'Malatya': { lat: 38.35, lon: 38.32 },
    'Kahramanmaraş': { lat: 37.58, lon: 36.93 },
    'Erzurum': { lat: 39.9, lon: 41.27 },
    'Van': { lat: 38.49, lon: 43.41 },
    'Sakarya': { lat: 40.78, lon: 30.4 },
    'Manisa': { lat: 38.62, lon: 27.43 },
    'Sivas': { lat: 39.75, lon: 37.02 },
    'Trabzon': { lat: 41.0, lon: 39.72 },
    'Balıkesir': { lat: 39.65, lon: 27.89 },
    'Aydın': { lat: 37.85, lon: 27.84 },
    'Tekirdağ': { lat: 40.98, lon: 27.51 },
    'Muğla': { lat: 37.22, lon: 28.36 },
    'Hatay': { lat: 36.2, lon: 36.16 },
    'Afyonkarahisar': { lat: 38.76, lon: 30.54 },
    'Çanakkale': { lat: 40.15, lon: 26.41 },
    'Ordu': { lat: 40.98, lon: 37.88 },
    'Elazığ': { lat: 38.68, lon: 39.22 },
};

export function cityList(): string[] {
    return Object.keys(TR_CITIES);
}

const TTL_MS = 20 * 60 * 1000;
const memCache = new Map<string, { data: WeatherData; ts: number }>();

function lsKey(city: string): string {
    return `fp_weather_${city}`;
}

export async function fetchWeather(city: string): Promise<WeatherData | null> {
    const loc = TR_CITIES[city];
    if (!loc) return null;

    const cached = memCache.get(city);
    if (cached && Date.now() - cached.ts < TTL_MS) return cached.data;

    try {
        const url =
            `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}` +
            `&current=temperature_2m,weather_code&timezone=auto`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`weather http ${res.status}`);
        const j = (await res.json()) as { current?: { temperature_2m?: number; weather_code?: number } };
        const t = j.current?.temperature_2m;
        const c = j.current?.weather_code;
        if (typeof t !== 'number' || typeof c !== 'number') throw new Error('weather payload');
        const data: WeatherData = { temp: Math.round(t), code: c };
        memCache.set(city, { data, ts: Date.now() });
        try {
            localStorage.setItem(lsKey(city), JSON.stringify({ data, ts: Date.now() }));
        } catch {
            /* localStorage dolu/yok — yok say */
        }
        return data;
    } catch {
        // Çevrimdışı/hata → son bilinen değer (localStorage)
        try {
            const raw = localStorage.getItem(lsKey(city));
            if (raw) {
                const p = JSON.parse(raw) as { data: WeatherData };
                return p.data;
            }
        } catch {
            /* yok say */
        }
        return null;
    }
}

export type WeatherCategory = 'clear' | 'cloud' | 'rain' | 'snow' | 'fog' | 'storm';

/** WMO weather_code → kategori. */
export function weatherCategory(code: number): WeatherCategory {
    if (code === 0 || code === 1) return 'clear';
    if (code === 2 || code === 3) return 'cloud';
    if (code === 45 || code === 48) return 'fog';
    if (code >= 71 && code <= 77) return 'snow';
    if (code === 85 || code === 86) return 'snow';
    if (code >= 95) return 'storm';
    return 'rain'; // 51-67, 80-82 çisenti/yağmur/sağanak
}
